import React, { useCallback, useContext, useEffect } from 'react'
import { useForm, FormProvider, Controller } from 'react-hook-form'
import { useDispatch } from 'react-redux'
import cx from 'classnames'
import { v4 } from 'uuid'
import _ from 'lodash'

import Select from 'react-select'

import { deleteAction, addAction, updateAction, setFlowHistory } from 'Actions/flow'
import { StepContext } from 'Src/containers/contexts'

import CollectMultiInformationEdit from './collect-multiple-info/CollectMultiInformationEdit'
import Step from '../templates/action-select/Step'
import { findgoToStep } from '../../utils/findGoToStep'

const AVAILABLE_TYPES = [
  {
    label: 'Name',
    type: 'collect_information_name',
    default: true
  },
  {
    label: 'Email',
    type: 'collect_information_email'
  },
  {
    label: 'Text',
    type: 'collect_information_text'
  },
  {
    label: 'Text (Multiline)',
    type: 'collect_information_text_multiline'
  }
]

const DEFAULT_ACTION = AVAILABLE_TYPES.find((t) => t.default)

const CollectInformationEdit = (props) => {
  const { mode = 'edit', actionId, cancelFn = _.noop, disableOptions = false } = props

  const dispatch = useDispatch()
  const { step, stepId, row } = useContext(StepContext)

  const settings = _.get(step, 'settings', {})
  const actionSetting = _.get(settings, `${actionId}`, {})
  const showMultiFormEdit = !actionSetting?.field

  const formMethods = useForm({
    defaultValues: {
      type: actionSetting.type || DEFAULT_ACTION.type,
      label: actionSetting.field?.label,
      action: { ...actionSetting.action }
    }
  })

  const {
    register,
    control,
    handleSubmit,
    formState: { errors }
  } = formMethods
  const canShowDeleteBtn = mode === 'edit'

  const update = useCallback(
    (data) => {
      dispatch(
        updateAction({
          ...data,
          actionId
        })
      )

      dispatch(setFlowHistory({ goToStep: findgoToStep(data.setting.action), row }))
    },
    [dispatch, actionId, row]
  )

  const create = useCallback(
    (data) => {
      dispatch(
        addAction({
          ...data,
          actionId: v4()
        })
      )
      dispatch(setFlowHistory({ goToStep: findgoToStep(data.setting.action), row }))
    },
    [dispatch, row]
  )

  const onSubmit = useCallback(
    (formData) => {
      const data = {
        stepId,
        setting: {
          type: formData.type,
          action: {
            type: 'goToStep',
            ...formData.action
          },
          field: {
            label: formData.label
          }
        }
      }

      if (mode === 'create') {
        create(data)
      } else {
        update(data)
      }

      cancelFn()
    },
    [cancelFn, stepId, mode, create, update]
  )

  const onDeleteClick = useCallback(() => {
    dispatch(
      deleteAction({
        stepId,
        actionId
      })
    )
    dispatch(setFlowHistory({ goToStep: stepId, row: row - 1 }))
  }, [dispatch, actionId, stepId, row])

  // TODO: Needs to be moved outside to prevent repetition in all action components
  useEffect(() => {
    const goToStep = findgoToStep(actionSetting.action)
    if (mode === 'create' || goToStep === undefined) {
      const currentRow = row - 1
      dispatch(setFlowHistory({ goToStep: stepId, row: currentRow }))
    }
  }, [dispatch, mode, row, stepId, actionSetting.action])

  if (showMultiFormEdit) {
    return (
      <CollectMultiInformationEdit
        mode={mode}
        cancelFn={cancelFn}
        actionId={actionId}
        disableOptions={disableOptions}
        defaultActionType={DEFAULT_ACTION.type}
      />
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='form-group'>
        <div className='label'>Collect Information</div>
        <Controller
          control={control}
          name='type'
          rules={{
            required: 'This field is required'
          }}
          render={({ field: { value, onChange } }) => {
            return (
              <Select
                options={AVAILABLE_TYPES}
                value={AVAILABLE_TYPES.find((option) => option.type === value)}
                defaultValue={AVAILABLE_TYPES.find((option) => option.default)}
                getOptionValue={(option) => option.type}
                onChange={(option) => onChange(option.type)}
                menuPortalTarget={document.body}
                menuPosition='fixed'
              />
            )
          }}
        />
      </div>
      <div className='form-group'>
        <div className='label'>Label</div>
        <input
          className={cx('form-control', { 'is-invalid': errors.label })}
          type='text'
          placeholder='Enter label'
          {...register('label', {
            required: 'Label is required',
            minLength: {
              value: 3,
              message: 'Label should be at leaset 3 characters'
            }
          })}
        />
        {errors.label && <small className='form-text text-danger'>{errors.label.message}</small>}
      </div>
      <FormProvider {...formMethods}>
        <div className='form-group'>
          <Step allowStepCreate />
          {errors.action && <small className='form-text text-danger'>{errors.action.message}</small>}
        </div>
      </FormProvider>
      {!disableOptions && (
        <div className='options'>
          <button className='button-primary small save' type='submit'>
            Save
          </button>
          <button className='btn cancel' onClick={cancelFn} type='button'>
            Cancel
          </button>
          {canShowDeleteBtn && (
            <button className='btn delete' onClick={onDeleteClick} type='button'>
              Delete
            </button>
          )}
        </div>
      )}
    </form>
  )
}

export default CollectInformationEdit
